import type { AiCost } from '../ai/ui/CostTag';
import { apiRequest } from '@/lib/api-client';

export type ContentStatus = 'draft' | 'approved' | 'scheduled' | 'published';

export interface ExternalPostRef {
  provider: string;
  channel: string;
  ref: string;
  url: string | null;
}

export interface ContentItem {
  id: string;
  type: string;
  title: string;
  body: string;
  hooks: string[];
  cta: string;
  hashtags: string[];
  mediaIdea: string;
  imageUrl: string | null;
  status: ContentStatus;
  scheduledFor: string | null;
  source: 'llm' | 'template';
  publishedAt: string | null;
  externalRefs: ExternalPostRef[];
  publishError: string | null;
  cost: AiCost | null;
  createdAt: string;
}

export interface ContentCampaign {
  id: string;
  name: string;
  occasionKey: string | null;
  rationale: string;
  startDate: string;
  endDate: string;
  postCount: number;
  source: 'llm' | 'template';
  createdAt: string;
}

export interface Occasion {
  key: string;
  name: string;
  tags: string[];
}

const base = (orgId: string, projectId: string) =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/content`;

export const contentApi = {
  list: (orgId: string, projectId: string) => apiRequest<ContentItem[]>(base(orgId, projectId)),
  generate: (orgId: string, projectId: string) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/generate`, { method: 'POST' }),
  compose: (
    orgId: string,
    projectId: string,
    body: { instructions: string; productId?: string; generateImage?: boolean },
  ) => apiRequest<ContentItem>(`${base(orgId, projectId)}/compose`, { method: 'POST', body }),
  activate: (orgId: string, projectId: string, cadenceHours: number) =>
    apiRequest<{ scheduleId: string }>(`${base(orgId, projectId)}/schedule`, {
      method: 'POST',
      body: { cadenceHours },
    }),
  updateStatus: (orgId: string, projectId: string, id: string, status: ContentStatus) =>
    apiRequest<ContentItem>(`${base(orgId, projectId)}/${id}`, {
      method: 'PATCH',
      body: { status },
    }),
  publish: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ published: boolean; refs: ExternalPostRef[] }>(
      `${base(orgId, projectId)}/${id}/publish`,
      { method: 'POST' },
    ),
  remove: (orgId: string, projectId: string, id: string) =>
    apiRequest<{ deleted: boolean }>(`${base(orgId, projectId)}/${id}`, { method: 'DELETE' }),
  regenerateImage: (orgId: string, projectId: string, id: string, instruction?: string) =>
    apiRequest<ContentItem>(`${base(orgId, projectId)}/${id}/regenerate-image`, {
      method: 'POST',
      body: instruction ? { instruction } : {},
    }),
  editText: (
    orgId: string,
    projectId: string,
    id: string,
    fields: { title?: string; body?: string; cta?: string; hashtags?: string[] },
  ) =>
    apiRequest<ContentItem>(`${base(orgId, projectId)}/${id}/text`, {
      method: 'PATCH',
      body: fields,
    }),
  getSettings: (orgId: string, projectId: string) =>
    apiRequest<{ autoPublish: boolean }>(`${base(orgId, projectId)}/settings`),
  setAutoPublish: (orgId: string, projectId: string, autoPublish: boolean) =>
    apiRequest<{ autoPublish: boolean }>(`${base(orgId, projectId)}/settings`, {
      method: 'PATCH',
      body: { autoPublish },
    }),
  occasions: (orgId: string, projectId: string) =>
    apiRequest<Occasion[]>(`${base(orgId, projectId)}/occasions`),
  listCampaigns: (orgId: string, projectId: string) =>
    apiRequest<ContentCampaign[]>(`${base(orgId, projectId)}/campaigns`),
  planCampaign: (
    orgId: string,
    projectId: string,
    occasionKey?: string,
    productIds?: string[],
    brand?: string,
  ) =>
    apiRequest<{ taskId: string }>(`${base(orgId, projectId)}/campaigns/plan`, {
      method: 'POST',
      body: {
        ...(occasionKey ? { occasionKey } : {}),
        ...(productIds && productIds.length > 0 ? { productIds } : {}),
        ...(brand ? { brand } : {}),
      },
    }),
  scheduleCampaigns: (orgId: string, projectId: string, cadenceHours = 168) =>
    apiRequest<{ scheduleId: string }>(`${base(orgId, projectId)}/campaigns/schedule`, {
      method: 'POST',
      body: { cadenceHours },
    }),
};
